import { useEffect, useRef, useState } from "react";
import LoginBox from "../../../UI/common/Boxes/login-box";
import DarkGreenButton from "../../../UI/common/Buttons/dark-green-button";
import LoginTextBox from "../../../UI/common/Inputs/login-textbox";
import classes from "./login-form.module.css";
import { Form, Link, useActionData, useLocation, useNavigation } from "react-router-dom";
import { buildRegisterPath, getRedirectToFromSearch } from "../../../utils/auth-redirect";
import AuthErrorModal from "./auth-error-modal";
import type { AuthActionData } from "../utils/auth-action-errors";

// Validation for email
const validateEmail = (value: string) => {
  if (!value) {
    return { isValid: false, errorMessage: "Email is required" };
  }
  const isValid = /\S+@\S+\.\S+/.test(value);
  return { isValid, errorMessage: isValid ? "" : "Invalid email address" };
};

// Validation for password
const validatePassword = (value: string) => {
  if (!value) {
    return { isValid: false, errorMessage: "Password is required" };
  }
  const isValid = value.length > 7;
  return { isValid, errorMessage: isValid ? "" : "Password must be more than 7 characters" };
};

const LoginForm: React.FC<{}> = (props) => {
  const [emailValidity, setEmailValidity] = useState(false);
  const [passwordValidity, setPasswordValidity] = useState(false);
  const emailResetRef = useRef<() => void>(() => {});
  const passwordResetRef = useRef<() => void>(() => {});

  const [isErrorDismissed, setIsErrorDismissed] = useState(false);
  const data = useActionData() as AuthActionData | undefined;
  const errorTitle = data?.error?.title ?? "Sign in failed";
  const errorMessage = data?.error?.message ?? data?.message ?? null;
  const navigation = useNavigation();
  const location = useLocation();
  const isSubmitting = navigation.state === "submitting";
  const registerPath = buildRegisterPath(getRedirectToFromSearch(location.search, ""));

  // Combine email and password validity to determine overall form validity
  const isFormValid = emailValidity && passwordValidity;

  const handleEmailValidationChange = (isValid: boolean, reset: () => void) => {
    setEmailValidity(isValid);
    emailResetRef.current = reset;
  };

  const handlePasswordValidationChange = (isValid: boolean, reset: () => void) => {
    setPasswordValidity(isValid);
    passwordResetRef.current = reset;
  };

  const resetForm = () => {
    emailResetRef.current();
    passwordResetRef.current();
  };

  useEffect(() => {
    if (errorMessage) {
      setIsErrorDismissed(false);
      resetForm();
    }
  }, [errorMessage]);

  return (
    <>
      {errorMessage && !isErrorDismissed && (
        <AuthErrorModal
          title={errorTitle}
          message={errorMessage}
          onClose={() => setIsErrorDismissed(true)}
        />
      )}
      <Form method="POST" >
        <LoginBox>
          <LoginTextBox
            type="text"
            id="email"
            placeholder="Enter your email"
            validateFunction={validateEmail}
            onValidationChange={handleEmailValidationChange}
          />
          <LoginTextBox
            type="password"
            id="password"
            placeholder="Enter your password"
            validateFunction={validatePassword}
            onValidationChange={handlePasswordValidationChange}
          />
          <div className={classes.Fpass}>
            Forgot password? <Link to={registerPath}>Sign Up?</Link>
          </div>
          {errorMessage && <p className={classes["error-text"]}>{errorMessage}</p>}
          <DarkGreenButton
            text="Login"
            type="submit"
            isDisabled={!isFormValid || isSubmitting}
          />
        </LoginBox>
      </Form>
    </>
  );
};

export default LoginForm;
